import { ChangeEvent, useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import styles from "./addOrgModal.module.scss";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { CircularProgress, FormControl, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { useAppSelector, useAppDispatch } from "src/page/store";
import { countryCodeData } from "src/types/countryCode";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { AddOrgRequest } from "src/network/graphql/organisationService";
import { createInitialOrgAction } from "src/page/onBoarding/onBoardingSlice";
import { checkUserDisplayIdExist } from "src/network/graphql/userService";
import { logUserInteraction, setUserDetail } from "src/utils/utils";
import { CORPORATE, EMPTY_STRING, INDIVIDUAL, REFERER } from "src/components/constant/constant";

interface AddOrgModalProps {
  handleClose: () => void;
  onSuccess: (_organizationId: string) => void;
  rootContainrOver?: string;
  closeButton?: boolean;
  orgLoading?: boolean;
  buttonText?: string;
  forParticipant?: boolean;
  Orgtype?: string;
  heading?: string;
}

export default function AddOrgModal({
  handleClose,
  onSuccess,
  rootContainrOver,
  closeButton = true,
  orgLoading = false,
  buttonText = "Submit",
  forParticipant = false,
  Orgtype,
  heading = "General Company Info"
}: AddOrgModalProps) {
  const { userInfo } = useAppSelector((state) => state.auth);
  const phoneNumber = userInfo?.contact && parsePhoneNumberFromString(userInfo?.contact, "AU")?.nationalNumber;

  const [input, setInput] = useState(() => ({
    name: forParticipant && userInfo?.fullName ? userInfo?.fullName : EMPTY_STRING,
    abn: forParticipant ? "12345678901" : EMPTY_STRING,
    phoneCountryCode: "+61",
    phone: forParticipant && phoneNumber ? phoneNumber : EMPTY_STRING,
    type: Orgtype || EMPTY_STRING,
    referral: EMPTY_STRING,
    line1: EMPTY_STRING,
    line2: EMPTY_STRING,
    city: EMPTY_STRING,
    state: EMPTY_STRING,
    postcode: EMPTY_STRING,
    country: "Australia"
  }));

  const [error, setError] = useState({
    name: EMPTY_STRING,
    abn: EMPTY_STRING,
    phone: EMPTY_STRING,
    type: EMPTY_STRING,
    referral: EMPTY_STRING,
    addressLine1: EMPTY_STRING,
    city: EMPTY_STRING,
    state: EMPTY_STRING,
    postcode: EMPTY_STRING,
    country: EMPTY_STRING
  });

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (forParticipant) {
      setInput((prevInput) => ({
        ...prevInput,
        abn: "12345678901"
      }));
    }
  }, []);

  useEffect(() => {
    if (Orgtype) {
      setInput({
        ...input,
        type: Orgtype
      });
    }
  }, [Orgtype]);

  const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleCancelClose = () => {
    handleClose();
  };

  const validateInput: () => boolean = () => {
    if (
      !input.name.trim() ||
      !input.abn.trim() ||
      !input.phone.trim() ||
      !input.type.trim() ||
      !input.line1.trim() ||
      !input.city.trim() ||
      !input.state.trim() ||
      !input.postcode.trim() ||
      !input.country.trim()
    ) {
      setError({
        ...error,
        name: input.name.trim() ? EMPTY_STRING : "Company name is required",
        abn: input.abn.trim() ? EMPTY_STRING : "Company's Abn is required",

        phone: input.phone.trim()
          ? parsePhoneNumberFromString(input.phoneCountryCode + input.phone)?.isValid()
            ? EMPTY_STRING
            : "Invalid contact number"
          : "Company's phone is required",
        type: input.type.trim() ? EMPTY_STRING : "Company type is required",
        addressLine1: input.line1.trim() ? EMPTY_STRING : "address is required",
        city: input.city.trim() ? EMPTY_STRING : "city is required",
        state: input.state.trim() ? EMPTY_STRING : "state is required",
        postcode: input.postcode.trim() ? EMPTY_STRING : "postcode is required",
        country: input.country.trim() ? EMPTY_STRING : "country is required"
      });
      return false;
    }

    handleReferralCode();

    if (input.abn.length < 11) {
      setError({
        ...error,
        abn: "ABN number should be equal to 11 digits"
      });
      return false;
    }

    return true;
  };

  const handleAddOrg = () => {
    if (!validateInput()) {
      return;
    }

    const data: AddOrgRequest = {
      name: input.name,
      abn: input.abn,
      referral_code: input.referral,
      phone: input.phoneCountryCode + input.phone,
      type: input.type,
      line1: input.line1,
      line2: input.line2,
      city: input.city,
      state: input.state,
      postcode: input.postcode,
      country: input.country,
      referenceName: `${userInfo?.firstName ?? EMPTY_STRING} ${userInfo?.lastName ?? EMPTY_STRING}`,
      referenceRole: "SuperAdmin",
      referenceEmail: userInfo?.email ?? EMPTY_STRING,
      referencePhone: userInfo?.contact ?? EMPTY_STRING,
      superAdminInitial: "Mr.",
      superAdminFirstName: userInfo?.firstName || EMPTY_STRING,
      superAdminLastName: userInfo?.lastName || EMPTY_STRING,
      superAdminEmail: userInfo?.email,
      superAdminGender: "male",
      superAdminPhone: userInfo?.contact,
      superAdminUserName: userInfo?.cognitoUserName,
      superAdminaddressLine1: input.line1,
      superAdminaddressLine2: input.line2,
      superAdminCity: input.city,
      superAdminState: input.state,
      superAdminPostcode: input.postcode,
      superAdminCountry: input.country,
      superAdminDob: EMPTY_STRING,
      address: EMPTY_STRING,
      id: null
    };

    dispatch(
      createInitialOrgAction(data, (organizationId) => {
        if (userInfo) {
          setUserDetail(userInfo, organizationId);
        }
        logUserInteraction("organization_created", { organizationId });
        onSuccess(organizationId);
      })
    );
  };

  const handleCountryCodeChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;

    setInput({ ...input, [name]: value });
  };

  const handleOrgContactBlur = () => {
    const phoneNumber = parsePhoneNumberFromString(input.phoneCountryCode + input.phone);

    if (!phoneNumber || !phoneNumber.isValid()) {
      setError({
        ...error,
        phone: "Invalid contact number"
      });
    } else {
      setError({
        ...error,
        phone: EMPTY_STRING
      });
    }
  };

  const handleReferralCode = async () => {
    if (input.referral.trim()) {
      const response = await checkUserDisplayIdExist(input.referral);
      if (!response.data.is_user_display_id_exist) {
        setError({
          ...error,
          referral: "Referral ID is Invalid"
        });
        return false;
      } else {
        setError({
          ...error,
          referral: EMPTY_STRING
        });
      }
    }
  };

  return (
    <div className={` ${styles.rootContainer} ${rootContainrOver}`}>
      <main className={styles.modalTop}>
        <form>
          <div className={styles.close}>
            <h5>Step 2 - {heading}</h5>
            {closeButton && (
              <button onClick={handleCancelClose} aria-label="close">
                <CloseIcon onClick={handleCancelClose} />
              </button>
            )}
          </div>
          {orgLoading ? (
            <div className={styles.progress}>
              <CircularProgress sx={{ color: "#0f172a" }} />
            </div>
          ) : (
            <>
              <div className={styles.generalCompany}>
                <div className={styles.companyName}>
                  <label htmlFor="name">{forParticipant ? "Participant Name" : "Company Name"}:</label>
                  <div className={styles.inputWerror}>
                    <input
                      id="name"
                      type="text"
                      placeholder="Enter Text Here"
                      value={input.name}
                      name="name"
                      onChange={handleInput}
                    />
                    {error.name && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.name}
                      </div>
                    )}
                  </div>
                </div>
                {!forParticipant && (
                  <div className={styles.abnName}>
                    <label htmlFor="abn">ABN :</label>
                    <div className={styles.inputWerror}>
                      <input
                        id="abn"
                        type="text"
                        placeholder="Enter Text Here"
                        maxLength={11}
                        value={input.abn}
                        name="abn"
                        onChange={handleInput}
                      />
                      {error.abn && (
                        <div className={styles.error}>
                          <ErrorOutlineIcon className={styles.errIcon} />
                          {error.abn}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className={styles.mobile}>
                  <label htmlFor="orgcontact">Contact Phone :</label>
                  <div className={styles.inputWerror}>
                    <div className={styles.mobileSelect}>
                      <Select
                        value={input.phoneCountryCode || "+61"}
                        onChange={handleCountryCodeChange}
                        displayEmpty
                        name="phoneCountryCode"
                        className={styles.countryCodeSelect}
                        sx={{ height: "50px" }}
                      >
                        {countryCodeData.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.code}
                          </MenuItem>
                        ))}
                      </Select>
                      <input
                        id="orgcontact"
                        type="text"
                        placeholder="Enter Text Here"
                        value={input.phone}
                        name="phone"
                        onChange={handleInput}
                        onBlur={handleOrgContactBlur}
                      />
                    </div>
                    {error.phone && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.phone}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.mobile}>
                  <label htmlFor="type">Type :</label>
                  <div className={styles.inputWerror}>
                    <div className={styles.mobileSelect}>
                      <FormControl sx={{ m: 1, margin: 0, width: "100%" }}>
                        <Select
                          id="type"
                          value={input.type}
                          onChange={(e) => {
                            if (e.target.value === "type") {
                              setInput({
                                ...input,
                                type: EMPTY_STRING
                              });
                            } else {
                              setInput({
                                ...input,
                                type: e.target.value
                              });
                            }
                          }}
                          displayEmpty
                          inputProps={{ "aria-label": "Without label" }}
                          sx={{
                            height: 50,
                            border: "#e0e2e7",
                            borderRadius: "8px",
                            color: {
                              CORPORATE: EMPTY_STRING,
                              INDIVIDUAL: EMPTY_STRING,
                              EMPTY_STRING: "grey",
                              default: "grey"
                            }[input.type ?? "default"]
                          }}
                          disabled
                        >
                          <MenuItem value={EMPTY_STRING} sx={{ color: "grey" }}>
                            Type
                          </MenuItem>
                          <MenuItem value={CORPORATE}>Corporate</MenuItem>
                          <MenuItem value={INDIVIDUAL}>Individual</MenuItem>
                          <MenuItem value={REFERER}>Referer</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    {error.type && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.type}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.abnName}>
                  <label htmlFor="referral">Referral Code :</label>
                  <div className={styles.inputWerror}>
                    <input
                      id="referral"
                      type="text"
                      placeholder="Enter Text Here"
                      value={input.referral.toUpperCase()}
                      name="referral"
                      onChange={handleInput}
                      onBlur={handleReferralCode}
                    />
                    {error.referral && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.referral}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.close}>
                <h5>What Address should we send the card to?</h5>
              </div>
              <div className={styles.companyAddress}>
                <div className={styles.addressLine1}>
                  <label htmlFor="addressline1">Line 1 :</label>
                  <div className={styles.inputWerror}>
                    <input
                      id="addressline1"
                      type="text"
                      placeholder="Enter Text Here"
                      value={input.line1}
                      name="line1"
                      onChange={handleInput}
                    />
                    {error.addressLine1 && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.addressLine1}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.addressLine2}>
                  <label htmlFor="addressline2">Line 2 :</label>
                  <input
                    id="addressline2"
                    type="text"
                    placeholder="Enter Text Here"
                    value={input.line2}
                    name="line2"
                    onChange={handleInput}
                  />
                </div>
                <div className={styles.city}>
                  <label htmlFor="city">City :</label>
                  <div className={styles.inputWerror}>
                    <input
                      id="city"
                      type="text"
                      placeholder="Enter Text Here"
                      value={input.city}
                      name="city"
                      onChange={handleInput}
                    />
                    {error.city && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.city}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.state}>
                  <label htmlFor="state">State :</label>
                  <div className={styles.inputWerror}>
                    <input
                      id="state"
                      type="text"
                      placeholder="Enter Text Here"
                      value={input.state}
                      name="state"
                      onChange={handleInput}
                    />
                    {error.state && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.state}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.postcode}>
                  <label htmlFor="postcode">Postcode :</label>
                  <div className={styles.inputWerror}>
                    <input
                      id="postcode"
                      type="text"
                      placeholder="Enter Text Here"
                      value={input.postcode}
                      name="postcode"
                      onChange={handleInput}
                    />
                    {error.postcode && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.postcode}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.country}>
                  <label htmlFor="country">Country :</label>
                  <div className={styles.inputWerror}>
                    <input
                      id="country"
                      type="text"
                      placeholder="Enter Text Here"
                      value={input.country}
                      name="country"
                      onChange={handleInput}
                    />
                    {error.country && (
                      <div className={styles.error}>
                        <ErrorOutlineIcon className={styles.errIcon} />
                        {error.country}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </form>
      </main>
      <footer className={styles.modalBottom}>
        <div className={styles.referModalBtn}>
          <Stack spacing={2} direction="row">
            <Button className={styles.sendBtn} variant="contained" onClick={handleAddOrg} disabled={orgLoading}>
              {buttonText}
            </Button>
          </Stack>
        </div>
      </footer>
    </div>
  );
}
